import React, { useState, useMemo } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import {
  Button,
  Row,
  Col,
  Pagination,
  Spinner,
  InputGroup,
  DropdownButton,
  Dropdown,
} from 'react-bootstrap';
import { FaFilter, FaTimes, FaFileExcel, FaExclamationCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Header from '~/Components/Header/Header';
import CardQualidade from './CardQualidade';
import { salvarEmExcel } from '~/Utils/Helpers';
import { filtroSchema, filtroInicial } from './validationSchema';
import {
  consultarRelatorioQualidadesAtivos,
} from '~/Services/Service';

const RECORDS_PER_PAGE = 24;

const OPCOES_ORDENACAO = [
  { coluna: 'codigo', direcao: 'asc', label: 'Código (crescente)' },
  { coluna: 'codigo', direcao: 'desc', label: 'Código (decrescente)' },
  { coluna: 'descricao', direcao: 'asc', label: 'Descrição (A → Z)' },
  { coluna: 'descricao', direcao: 'desc', label: 'Descrição (Z → A)' },
];

const ORDENACAO_PADRAO = OPCOES_ORDENACAO[0];

const RelatorioQualidadesAtivos = () => {
  const [resultados, setResultados] = useState([]);
  const [filtrosAtivos, setFiltrosAtivos] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [ordenacao, setOrdenacao] = useState({
    coluna: ORDENACAO_PADRAO.coluna,
    direcao: ORDENACAO_PADRAO.direcao,
  });

  const totalPages = Math.ceil(resultados.length / RECORDS_PER_PAGE);

  const dadosOrdenados = useMemo(() => {
    if (!ordenacao.coluna) return resultados;
    return [...resultados].sort((a, b) => {
      const va = a[ordenacao.coluna] != null ? a[ordenacao.coluna] : '';
      const vb = b[ordenacao.coluna] != null ? b[ordenacao.coluna] : '';
      if (va < vb) return ordenacao.direcao === 'asc' ? -1 : 1;
      if (va > vb) return ordenacao.direcao === 'asc' ? 1 : -1;
      return 0;
    });
  }, [resultados, ordenacao]);

  const dadosPaginados = useMemo(() => {
    const last = paginaAtual * RECORDS_PER_PAGE;
    const first = last - RECORDS_PER_PAGE;
    return dadosOrdenados.slice(first, last);
  }, [dadosOrdenados, paginaAtual]);

  const opcaoOrdenacaoAtual = OPCOES_ORDENACAO.find(
    opt =>
      opt.coluna === ordenacao.coluna && opt.direcao === ordenacao.direcao
  );
  const labelOrdenacaoAtual = opcaoOrdenacaoAtual
    ? opcaoOrdenacaoAtual.label
    : ORDENACAO_PADRAO.label;

  const filtrarPayload = values => {
    if (values.tipoFiltro === 'codigo') {
      return {
        codigo_de: values.inicial === '' ? null : Number(values.inicial),
        codigo_ate: values.final === '' ? null : Number(values.final),
        nome_de: null,
        nome_ate: null,
      };
    }
    return {
      codigo_de: null,
      codigo_ate: null,
      nome_de: values.inicial === '' ? null : String(values.inicial).trim(),
      nome_ate: values.final === '' ? null : String(values.final).trim(),
    };
  };

  const handleFiltrar = async values => {
    setLoading(true);
    const payload = filtrarPayload(values);
    try {
      const resultado = await consultarRelatorioQualidadesAtivos(payload);
      setResultados(resultado);
      setFiltrosAtivos(payload);
      setPaginaAtual(1);
      toast.success(`${resultado.length} registro(s) encontrado(s).`);
    } catch (err) {
      const msg =
        err && err.message ? err.message : 'Erro ao consultar relatório.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleLimpar = resetForm => {
    resetForm();
    setResultados([]);
    setFiltrosAtivos(null);
    setPaginaAtual(1);
    setOrdenacao({
      coluna: ORDENACAO_PADRAO.coluna,
      direcao: ORDENACAO_PADRAO.direcao,
    });
  };

  const handleExportar = async () => {
    if (!filtrosAtivos) return;
    setExportando(true);
    try {
      await salvarEmExcel(filtrosAtivos, '/relatorios/qualidades-ativos/exportar-excel');
      toast.success('Relatório exportado com sucesso!');
    } catch (err) {
      toast.error('Erro ao exportar Excel.');
    } finally {
      setExportando(false);
    }
  };

  const renderPaginacao = () => {
    const items = [];
    const maxVisible = 5;
    const start = Math.max(1, paginaAtual - Math.floor(maxVisible / 2));
    const end = Math.min(totalPages, start + maxVisible - 1);

    if (start > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => setPaginaAtual(1)}>
          1
        </Pagination.Item>
      );
      if (start > 2) {
        items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
      }
    }

    for (let p = start; p <= end; p++) {
      items.push(
        <Pagination.Item
          key={p}
          active={p === paginaAtual}
          onClick={() => setPaginaAtual(p)}
        >
          {p}
        </Pagination.Item>
      );
    }

    if (end < totalPages) {
      if (end < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
      }
      items.push(
        <Pagination.Item
          key={totalPages}
          onClick={() => setPaginaAtual(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    return items;
  };

  return (
    <div>
      <Header title="Relatório de Qualidade de Ativos" />

      <div className="container-fluid bg-white p-3">
        <Formik
          initialValues={filtroInicial}
          validationSchema={filtroSchema}
          onSubmit={handleFiltrar}
        >
          {({ resetForm, values, setFieldValue }) => {
            const isCodigo = values.tipoFiltro === 'codigo';
            const labelBase = isCodigo ? 'Código' : 'Nome';
            const inputType = isCodigo ? 'number' : 'text';
            const inputMaxLength = isCodigo ? undefined : 30;

            const trocarTipo = novoTipo => {
              if (novoTipo === values.tipoFiltro) return;
              setFieldValue('tipoFiltro', novoTipo);
              setFieldValue('inicial', '');
              setFieldValue('final', '');
            };

            return (
            <Form>
              <Row className="align-items-end">
                <Col xs={12} sm={6} md={2}>
                  <label className="small mb-1">{`${labelBase} Inicial`}</label>
                  <Field
                    name="inicial"
                    type={inputType}
                    maxLength={inputMaxLength}
                    className="form-control"
                  />
                  <ErrorMessage
                    name="inicial"
                    component="div"
                    className="invalid-feedback d-block"
                  />
                </Col>
                <Col xs={12} sm={6} md={2}>
                  <label className="small mb-1">{`${labelBase} Final`}</label>
                  <InputGroup>
                    <Field
                      name="final"
                      type={inputType}
                      maxLength={inputMaxLength}
                      className="form-control"
                    />
                    <DropdownButton
                      as={InputGroup.Append}
                      alignRight
                      variant="outline-secondary"
                      title={labelBase}
                      id="dropdown-tipo-filtro-qualidades"
                    >
                      <Dropdown.Item
                        active={isCodigo}
                        onClick={() => trocarTipo('codigo')}
                      >
                        Código
                      </Dropdown.Item>
                      <Dropdown.Item
                        active={!isCodigo}
                        onClick={() => trocarTipo('nome')}
                      >
                        Nome
                      </Dropdown.Item>
                    </DropdownButton>
                  </InputGroup>
                  <ErrorMessage
                    name="final"
                    component="div"
                    className="invalid-feedback d-block"
                  />
                </Col>
                <Col xs={12} md="auto" className="mt-3 mt-md-0 mb-2 mb-md-0 ml-md-auto pr-md-1">
                  <Button block type="submit" variant="primary" disabled={loading}>
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="mr-2"
                        />
                        Filtrando...
                      </>
                    ) : (
                      <>
                        <FaFilter className="mr-1" /> Filtrar
                      </>
                    )}
                  </Button>
                </Col>
                <Col xs={12} md="auto" className="mb-2 mb-md-0 px-md-1">
                  <Button
                    block
                    type="button"
                    variant="outline-secondary"
                    onClick={() => handleLimpar(resetForm)}
                    disabled={loading}
                  >
                    <FaTimes className="mr-1" /> Limpar filtros
                  </Button>
                </Col>
                <Col xs={12} md="auto" className="pl-md-1">
                  <Button
                    block
                    type="button"
                    variant="success"
                    onClick={handleExportar}
                    disabled={!filtrosAtivos || exportando || loading}
                  >
                    {exportando ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="mr-2"
                        />
                        Exportando...
                      </>
                    ) : (
                      <>
                        <FaFileExcel className="mr-1" /> Exportar Excel
                      </>
                    )}
                  </Button>
                </Col>
              </Row>
            </Form>
            );
          }}
        </Formik>
      </div>

      {resultados.length > 0 ? (
        <>
          <div className="container-fluid bg-white p-3 mt-3">
            <Row className="align-items-center mb-3">
              <Col xs={12} md="auto">
                <small className="text-muted">
                  {`${resultados.length} registro(s) — exibindo ${dadosPaginados.length}`}
                </small>
              </Col>
              <Col xs={12} md="auto" className="ml-md-auto mt-2 mt-md-0">
                <Dropdown>
                  <Dropdown.Toggle
                    variant="outline-secondary"
                    size="sm"
                    id="dropdown-ordenacao-qualidades"
                  >
                    {`Ordenar por: ${labelOrdenacaoAtual}`}
                  </Dropdown.Toggle>
                  <Dropdown.Menu alignRight>
                    {OPCOES_ORDENACAO.map(opt => (
                      <Dropdown.Item
                        key={`${opt.coluna}-${opt.direcao}`}
                        active={
                          ordenacao.coluna === opt.coluna &&
                          ordenacao.direcao === opt.direcao
                        }
                        onClick={() =>
                          setOrdenacao({
                            coluna: opt.coluna,
                            direcao: opt.direcao,
                          })
                        }
                      >
                        {opt.label}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </Col>
            </Row>

            <Row>
              {dadosPaginados.map(item => (
                <Col
                  key={`qualidade-${item.codigo}`}
                  xs={12}
                  md={6}
                  className="mb-3"
                >
                  <CardQualidade
                    codigo={item.codigo}
                    descricao={item.descricao}
                  />
                </Col>
              ))}
            </Row>
          </div>
          {totalPages > 1 && (
            <div className="container-fluid bg-white p-3">
              <Row className="mt-2">
                <Col>
                  <Pagination className="d-inline-flex mb-0">
                    <Pagination.First
                      onClick={() => setPaginaAtual(1)}
                      disabled={paginaAtual === 1}
                    />
                    <Pagination.Prev
                      onClick={() => setPaginaAtual(paginaAtual - 1)}
                      disabled={paginaAtual === 1}
                    />
                    {renderPaginacao()}
                    <Pagination.Next
                      onClick={() => setPaginaAtual(paginaAtual + 1)}
                      disabled={paginaAtual === totalPages}
                    />
                    <Pagination.Last
                      onClick={() => setPaginaAtual(totalPages)}
                      disabled={paginaAtual === totalPages}
                    />
                  </Pagination>
                </Col>
              </Row>
            </div>
          )}
        </>
      ) : (
        !loading &&
        filtrosAtivos != null && (
          <div className="container-fluid bg-white p-5 mt-3 text-center">
            <Row>
              <Col>
                <FaExclamationCircle size={48} className="text-muted mb-3" />
                <h5 className="text-muted">Nenhum registro encontrado</h5>
                <p className="text-muted">Ajuste os filtros e tente novamente</p>
              </Col>
            </Row>
          </div>
        )
      )}
    </div>
  );
};

export default RelatorioQualidadesAtivos;
